import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import GroupAccordionItem from "../display/groupAccordion/GroupAccordionItem";
import HomeroomGroupForm from "./HomeroomGroupForm";
import isGradingProfileWindowOpen from "../../utils/ActionControls/isGradingProfileWindowOpen";
import isEprEditable from "../../utils/ActionControls/isEprEditable";
import LoadingSvg from "../loadingSvg/LoadingSvg";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/HomeroomGroups.scss";

/**
 * HomeroomGroups is the main interface for managing student groupings within an EPR (Elementary Progress Report).
 * It supports creating, editing, and deleting groups, as well as selecting students within each group.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.dispatch - Dispatch function for reducer/state updates.
 * @param {Object} props.state - Global state including selectedEpr and gradingProfiles.
 * @returns {JSX.Element}
 */
const HomeroomGroups = ({ dispatch, state, basePath, schoolLevelTemplateDto }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { gradingProfileId } = useParams();
    const { gradingProfiles, selectedEpr } = state;

    const { state: globalState } = useContext(GlobalContext);
    const {
        actionWindowsDto,
        currentTimePeriodDto,
        schoolLevelTemplateDto: globalSchoolLevelTemplateDto,
        schoolYearDto,
        token,
        userDetails
    } = globalState || {};
    const resolvedSchoolLevelTemplateDto = schoolLevelTemplateDto || globalSchoolLevelTemplateDto;

    const [isActiveWindow, setIsActiveWindow] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [activeGroupKey, setActiveGroupKey] = useState(null);
    const [loader, setLoader] = useState(false);
    const [editingGroupKey, setEditingGroupKey] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState("");
    const [editingSelectedStudents, setEditingSelectedStudents] = useState([]);
    const [show, setShow] = useState(false);

    /**
     * Creates a new homeroom group with the selected students.
     * If all students are selected, the group is marked as "WHOLE_CLASS".
     */
    const handleCreateGroup = () => {
        const arrayOfStudents = selectedStudents.map((student) => ({
            key: null,
            studentNumber: student
        }));

        const options = {
            action: "createHomeroomGroup",
            gradingProfileId: selectedEpr.key,
            data: {
                key: null,
                name: groupName,
                type: arrayOfStudents.length === selectedEpr.gradingProfileStudents.length ? "WHOLE_CLASS" : "GROUP",
                schoolYearKey: schoolYearDto.key,
                timePeriod: currentTimePeriodDto.key,
                ownerGradingProfile: selectedEpr.key,
                homeroomGroupStudents: arrayOfStudents
            },
            token
        };
        setLoader(true);
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const updatedProfile = response.data.payload;

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    toast.success("Report Card homeroom group successfully created.", { autoClose: 3000 });

                    setSelectedStudents([]);
                    setGroupName("");
                    if (setShow) {
                        setShow(false);
                    }

                    const newlyCreatedGroup = updatedProfile.homeroomGroups?.[updatedProfile.homeroomGroups.length - 1];
                    if (newlyCreatedGroup) {
                        setActiveGroupKey(String(newlyCreatedGroup.key));
                    }
                }
            })
            .catch((error) => {
                toast.error("Report Card homeroom group could not be created. Please try again later.");
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    /**
     * Updates an existing homeroom group, preserving existing student keys
     * and setting key: null for newly added students.
     *
     * @param {Object} group - The group object being edited.
     */
    const handleUpdateGroup = (group) => {
        // Build a map of existing students in the group for quick lookup
        const existingStudentMap = {};
        group.homeroomGroupStudents.forEach((s) => {
            existingStudentMap[s.studentNumber] = s.key;
        });

        const arrayOfStudents = editingSelectedStudents.map((studentNumber) => ({
            key: existingStudentMap[studentNumber] || null,
            studentNumber
        }));

        const options = {
            action: "updateHomeroomGroup",
            homeroomGroupId: group.key,
            data: {
                key: group.key,
                name: editingGroupName,
                type: arrayOfStudents.length === selectedEpr.gradingProfileStudents.length ? "WHOLE_CLASS" : "GROUP",
                schoolYearKey: group.schoolYearKey,
                timePeriod: group.timePeriod,
                ownerGradingProfile: selectedEpr.key,
                homeroomGroupStudents: arrayOfStudents
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const updatedProfile = response.data.payload;

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    toast.success("Report Card homeroom group successfully updated.", { autoClose: 3000 });

                    setEditingGroupKey(null);
                    setEditingGroupName("");
                    setEditingSelectedStudents([]);
                }
            })
            .catch((error) => {
                toast.error("Report Card homeroom group could not be updated at this time. Please try again later.", {
                    autoClose: 3000
                });
                console.error(error);
            });
    };

    /**
     * Deletes a homeroom group by key and updates local and global state.
     *
     * @param {string} groupKey - The key of the group to be deleted.
     */
    const handleDeleteGroup = (groupKey) => {
        const options = {
            action: "deleteHomeroomGroup",
            homeroomGroupId: groupKey,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data === "") {
                    const updatedGroups = selectedEpr.homeroomGroups.filter((group) => group.key !== groupKey);

                    const updatedEpr = {
                        ...selectedEpr,
                        homeroomGroups: updatedGroups
                    };

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedEpr });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedEpr.key ? updatedEpr : profile
                        )
                    });

                    toast.success("Report Card homeroom group successfully deleted.", { autoClose: 3000 });

                    if (String(groupKey) === activeGroupKey) {
                        setActiveGroupKey(null);
                    }
                }
            })
            .catch((error) => {
                toast.error("Report Card homeroom group could not be deleted. Please try again later.", {
                    autoClose: 3000
                });
                console.error(error);
            });
    };

    const toggleSelectedStudent = (prevStudents, id) =>
        prevStudents.includes(id) ? prevStudents.filter((sid) => sid !== id) : [...prevStudents, id];

    const handleEditCheckboxChange = (id) => {
        setEditingSelectedStudents((prev) => toggleSelectedStudent(prev, id));
    };

    const handleCreateCheckboxChange = (id) => {
        setSelectedStudents((prev) => toggleSelectedStudent(prev, id));
    };

    /**
     * Populates editing state with the selected group's data and activates its accordion panel.
     *
     * @param {Object} group - The group to be edited.
     */
    const handleEditGroup = (group) => {
        setEditingGroupKey(String(group.key));
        setActiveGroupKey(String(group.key));
        setEditingGroupName(group.name);
        setEditingSelectedStudents(group.homeroomGroupStudents.map((s) => s.studentNumber));
    };

    const handleAccordionSelect = (key) => {
        setActiveGroupKey(key);
    };

    const handleCloseEditForm = () => {
        setEditingGroupKey(null);
        setEditingGroupName("");
        setEditingSelectedStudents([]);
    };

    /**
     * Sets the default selected EPR profile on initial load if none is selected.
     * Prioritizes URL param if present, otherwise defaults to the first profile in the list.
     */
    useEffect(() => {
        if (!selectedEpr && gradingProfiles.length > 0) {
            const profileToSet = gradingProfileId
                ? gradingProfiles.find((p) => p.key === gradingProfileId)
                : gradingProfiles[0];
            dispatch({ type: "SET_SELECTED_EPR", payload: profileToSet });
        }
    }, [gradingProfileId, gradingProfiles, selectedEpr, dispatch]);

    useEffect(() => {
        if (location?.state?.openGroupKey && selectedEpr?.homeroomGroups) {
            const groupToEdit = selectedEpr.homeroomGroups.find(
                (g) => String(g.key) === String(location.state.openGroupKey)
            );
            if (groupToEdit) {
                handleEditGroup(groupToEdit);
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location, navigate, selectedEpr]);

    useEffect(() => {
        if (actionWindowsDto && selectedEpr) {
            const profileTimePeriod = selectedEpr.timePeriod;
            const isWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, profileTimePeriod);
            setIsActiveWindow(isWindowOpen);
        }
    }, [actionWindowsDto, selectedEpr]);

    const isActiveAndIsUnreleased = isEprEditable({
        isActiveWindow,
        status: selectedEpr?.status,
        userDetails
    });

    /**
     * Renders all existing homeroom groups in individual accordion panels.
     * Each group can be edited or deleted from its own panel.
     *
     * @returns {JSX.Element} Accordion component containing group panels.
     */
    const renderGroupAccordions = () => {
        const schoolLevelForCurrent =
            currentTimePeriodDto?.name === "1st Semester"
                ? resolvedSchoolLevelTemplateDto?.firstSemester
                : resolvedSchoolLevelTemplateDto?.secondSemester;
        const gradeObjects = schoolLevelForCurrent?.gradeToSubjects;
        if (!Array.isArray(gradeObjects)) {
            return null;
        }
        const matchingGrade = gradeObjects.find((grade) => grade.profileGradeId === selectedEpr.grade);
        return selectedEpr.homeroomGroups
            .filter((g) => !g.name.includes("INTERNAL_"))
            .map((group) => {
                return (
                    <Accordion activeKey={activeGroupKey} key={group.key} onSelect={handleAccordionSelect}>
                        <GroupAccordionItem
                            key={group.key}
                            dispatch={dispatch}
                            eventKey={String(group.key)}
                            grade={matchingGrade}
                            handleDeleteGroup={handleDeleteGroup}
                            isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                            onEdit={() => handleEditGroup(group)}
                            group={group}
                            selectedEpr={selectedEpr}
                            basePath={basePath}
                        />
                        {editingGroupKey === String(group.key) && (
                            <HomeroomGroupForm
                                dispatch={dispatch}
                                groupName={editingGroupName}
                                groupKey={group.key}
                                handleCheckboxChange={handleEditCheckboxChange}
                                handleUpdateGroup={() => handleUpdateGroup(group)}
                                isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                                isEditMode
                                onClose={handleCloseEditForm}
                                selectedEpr={selectedEpr}
                                selectedStudents={editingSelectedStudents}
                                setGroupName={setEditingGroupName}
                                setSelectedStudents={setEditingSelectedStudents}
                                show={true}
                                state={state}
                            />
                        )}
                    </Accordion>
                );
            });
    };

    /**
     * Renders the homeroom group creation form (modal or inline).
     *
     * @returns {JSX.Element} The creation form.
     */
    const renderCreateGroupForm = () => {
        const filteredGroupLength = selectedEpr?.homeroomGroups?.filter((g) => !g.name.includes("INTERNAL_")).length;
        return (
            <>
                {filteredGroupLength === 0 && (
                    <h6 style={{ marginBottom: "0.3rem", color: "#0A4064", fontSize: "18px", fontWeight: "500" }}>
                        Create Homeroom Group:{" "}
                    </h6>
                )}
                <div style={filteredGroupLength === 0 ? { backgroundColor: "white", borderRadius: "5px" } : {}}>
                    <HomeroomGroupForm
                        groupName={groupName}
                        setGroupName={setGroupName}
                        selectedStudents={selectedStudents}
                        setSelectedStudents={setSelectedStudents}
                        handleCreateGroup={handleCreateGroup}
                        handleCheckboxChange={handleCreateCheckboxChange}
                        isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                        dispatch={dispatch}
                        selectedEpr={selectedEpr}
                        setShow={setShow}
                        show={show}
                        state={state}
                        isEditMode={false}
                    />
                </div>
            </>
        );
    };

    return (
        <div>
            {selectedEpr?.homeroomGroups?.length > 0 && (
                <div
                    style={{
                        marginRight: "-10px",
                        marginBottom: "1rem",
                        display: "flex",
                        marginTop: "2rem",
                        alignContent: "flex-end",
                        justifyContent: "space-between"
                    }}
                >
                    <h5 style={{ color: "#0A4064", marginBottom: "0px" }}>
                        Homeroom Groups (
                        {selectedEpr?.homeroomGroups?.filter((g) => !g.name.includes("INTERNAL_")).length})
                    </h5>
                    {selectedEpr?.homeroomGroups?.filter((g) => !g.name.includes("INTERNAL_")).length > 0 && (
                        <ActionButton
                            className="action-button-reg"
                            disable={!isActiveAndIsUnreleased}
                            label="Create Homeroom Group"
                            onClick={() => setShow(true)}
                        />
                    )}
                </div>
            )}

            {selectedEpr?.homeroomGroups?.length > 0 && renderGroupAccordions()}

            {selectedEpr && renderCreateGroupForm()}

            {loader && <LoadingSvg />}
        </div>
    );
};

HomeroomGroups.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    basePath: PropTypes.string,
    schoolLevelTemplateDto: PropTypes.object
};

export default HomeroomGroups;
